"use client";

import { Paths } from "@app/routes";
import { AppContentFooter, WithPermission } from "@atlas/composite";
import { Button, Form, Spinner, StepperWizard, useToast } from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import {
  buildDefaultCatalogInstallConfigs,
  HubPackageDeploymentStatus,
  type IHubCatalog,
  type IHubPackageDeployment,
  resolveCatalogInstallTypeKey,
} from "@models";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useCreateHubPackageDeployment,
  useDeleteHubCart,
  useHubCatalogsByIds,
  useHubCatalogType,
  useHubCatalogTypes,
  useHubCurrentCart,
  useHubPackage,
  useUserInfo,
  useUserTenants,
} from "@query";
import {
  APP_BASE_PATH,
  defaultHubCartPackageId,
  HUB_PERM,
  mapUserTenantsToApplicationTenants,
  useNavigateBack,
} from "@shared";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { isSolutionCatalog } from "../../utils/checkoutCartOrderedEntries";
import { CatalogTemplateStep } from "../catalog/CatalogTemplateStep";
import { PricingDeploymentStep } from "./steps/PricingDeploymentStep";

export type ICatalogInstallPageProps = {
  packageId?: number;
};

function hubCatalogDisplayName(c: IHubCatalog, fallbackIndex: number): string {
  const label = c.label?.trim();
  const name = c.name?.trim();
  if (label) return label;
  if (name) return name;
  if (c.catalogId != null) return String(c.catalogId);
  return String(fallbackIndex);
}

export function CatalogInstallPage({
  packageId,
}: ICatalogInstallPageProps = {}) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigateBack = useNavigateBack();
  const navigate = useNavigate();

  const [wizardStep, setWizardStep] = useState(1);
  const [highestReachedStep, setHighestReachedStep] = useState(1);

  const completedSteps = useMemo(() => {
    // In a strict linear flow, all steps before the highest reached step are considered completed
    // (and therefore clickable to go back to, and marked with a green checkmark).
    return Array.from({ length: highestReachedStep - 1 }, (_, i) => i + 1);
  }, [highestReachedStep]);
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyUuid = userInfo?.companyUuid;
  const userUuid = userInfo?.id;
  const solutionsFetchEnabled = !!userInfo;

  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();

  const { data: userTenantRows } = useUserTenants(
    companyUuid,
    userUuid,
    APP_BASE_PATH,
    {
      enabled: solutionsFetchEnabled && !!companyUuid && !!userUuid,
    },
  );

  const tenants = useMemo(
    () => mapUserTenantsToApplicationTenants(userTenantRows),
    [userTenantRows],
  );

  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0,
  });
  const hubCartPackageId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );
  const resolvedPackageId = packageId ?? hubCartPackageId;
  const { data: hubCartPackage, isLoading: isLoadingPackage } = useHubPackage(
    resolvedPackageId,
    {
      enabled: resolvedPackageId > 0,
    },
  );

  const catalogIdsFromPackage = useMemo(() => {
    return (
      hubCartPackage?.catalogRelations
        ?.map((r: { catalogId: number; catalogVersion: number }) => ({
          catalogId: r.catalogId,
          version: r.catalogVersion,
        }))
        .filter(
          (item: {
            catalogId: number;
            version: number;
          }): item is { catalogId: number; version: number } =>
            item.catalogId != null &&
            item.catalogId > 0 &&
            item.version != null,
        ) ?? []
    );
  }, [hubCartPackage?.catalogRelations]);

  const { data: allCatalogs, isLoading: isLoadingAllCatalogs } =
    useHubCatalogsByIds(catalogIdsFromPackage, {
      enabled: catalogIdsFromPackage.length > 0,
    });

  const { catalog, addonList } = useMemo(() => {
    const solution = allCatalogs.find(isSolutionCatalog);
    const addons = allCatalogs.filter(
      (c: IHubCatalog) => !isSolutionCatalog(c),
    );
    return { catalog: solution, addonList: addons };
  }, [allCatalogs]);

  const numericSolutionCatalogId = catalog?.catalogId ?? 0;
  const addonCatalogIds = useMemo(
    () =>
      addonList
        .map((a: IHubCatalog) => a.catalogId)
        .filter((id: number | undefined): id is number => id != null),
    [addonList],
  );

  const catalogStepCount = useMemo(
    () =>
      (numericSolutionCatalogId > 0 ? 1 : 0) + (addonCatalogIds?.length ?? 0),
    [addonCatalogIds?.length, numericSolutionCatalogId],
  );

  const { mutate: deleteCart } = useDeleteHubCart();

  const { mutate: createDeployment, isPending: isCreatingDeployment } =
    useCreateHubPackageDeployment({
      onSuccess: (data: {
        status?: string;
        deploymentId?: number;
        packageId?: number;
      }) => {
        if (
          data.status?.toLowerCase() === HubPackageDeploymentStatus.INSTALLED &&
          data.packageId === userHubCart?.packageId &&
          userHubCart?.cartId
        ) {
          deleteCart(userHubCart.cartId);
        }

        toast.add({
          title: t("hub.subscribe_wizard.install_success", {
            defaultValue: "Installation started",
          }),
          data: { type: "success" },
        });
        if (data.deploymentId) {
          navigate(
            `/${Paths.PackageManager}?deploymentId=${data.deploymentId}&fromTab=deployments`,
          );
        } else {
          navigate(
            `/${Paths.PackageManager}?packageId=${data.packageId}&fromTab=deployments`,
          );
        }
      },
      onError: (error: unknown) => {
        const apiError = error as { status?: number; message?: string };
        toast.add({
          title: t("hub.subscribe_wizard.install_error", {
            defaultValue: "Installation could not be started",
          }),
          ...(apiError.status === 400 && apiError.message
            ? { description: apiError.message }
            : {}),
          data: { type: "error" },
        });
      },
    });

  const { data: catalogType, isLoading: catalogTypeLoading } =
    useHubCatalogType(catalog?.typeId ?? 0);

  const { data: catalogTypes = [], isLoading: catalogTypesLoading } =
    useHubCatalogTypes({
      mode: "DATA",
      sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
      startIndex: 0,
      limit: 100,
    });

  const isLoadingCatalogData = useMemo(
    () => isLoadingPackage || isLoadingAllCatalogs,
    [isLoadingPackage, isLoadingAllCatalogs],
  );

  const waitingForCatalog = useMemo(
    () => isLoadingCatalogData || catalogTypeLoading || catalogTypesLoading,
    [isLoadingCatalogData, catalogTypeLoading, catalogTypesLoading],
  );

  const generatedLabel = useMemo(() => {
    const timestamp = dayjs().format("MM/DD/YYYY-HH:mm:ss");
    return `Dpy-${timestamp}`;
  }, []);

  const computedFormValues = useMemo<IHubPackageDeployment | undefined>(() => {
    if (waitingForCatalog) return undefined;
    const companyId = userInfo?.companyId;
    if (companyId == null || companyId <= 0) return undefined;

    const rows = buildDefaultCatalogInstallConfigs({
      solution: numericSolutionCatalogId > 0 ? catalog : undefined,
      addons: addonList,
      companyId,
      catalogTypes,
    });
    if (rows.length !== catalogStepCount) return undefined;
    return {
      packageId: 0,
      companyId,
      tenantName: "",
      applicationId: "",
      status: HubPackageDeploymentStatus.PENDING,
      config: rows,
      installedCatalogs: catalogIdsFromPackage.map(
        (c: { catalogId: number; version: number }) => ({
          catalogId: c.catalogId,
          catalogVersion: c.version,
        }),
      ),
      name: generatedLabel,
      description: "",
      replaceTargetEntity: numericSolutionCatalogId > 0,
    };
  }, [
    addonList,
    catalog,
    catalogIdsFromPackage,
    catalogStepCount,
    numericSolutionCatalogId,
    userInfo?.companyId,
    waitingForCatalog,
    generatedLabel,
    catalogTypes,
  ]);

  const form = useForm<IHubPackageDeployment>({
    defaultValues: {
      packageId: 0,
      companyId: 0,
      tenantName: "",
      applicationId: "",
      status: HubPackageDeploymentStatus.PENDING,
      config: [],
      installedCatalogs: catalogIdsFromPackage.map(
        (c: { catalogId: number; version: number }) => ({
          catalogId: c.catalogId,
          catalogVersion: c.version,
        }),
      ),
      name: generatedLabel,
      description: "",
      replaceTargetEntity: numericSolutionCatalogId > 0,
    },
    values: computedFormValues,
  });

  const addonStepConfigs = useMemo(
    () =>
      addonList.map((addon: IHubCatalog, index: number) => {
        const description = hubCatalogDisplayName(addon, index);
        return {
          description,
          title: t("hub.subscribe_wizard.step_addon_details_desc", {
            defaultValue: "Add-on configuration",
          }),
        };
      }),
    [addonList, t],
  );

  const steps = useMemo(() => {
    const list: { title: string; description: string }[] = [];
    if (numericSolutionCatalogId > 0) {
      const solutionTitle = catalog
        ? hubCatalogDisplayName(catalog, 0)
        : String(numericSolutionCatalogId);
      list.push({
        description: solutionTitle,
        title: t("hub.subscribe_wizard.step_solution_details_desc", {
          defaultValue: "Solution configuration",
        }),
      });
    }
    list.push(...addonStepConfigs);
    list.push({
      title: t("hub.catalog_subscribe.section_deployment", {
        defaultValue: "Deployment",
      }),
      description: t("hub.subscribe_wizard.step_deployment_desc", {
        defaultValue: "Deployment",
      }),
    });
    return list;
  }, [addonStepConfigs, catalog, numericSolutionCatalogId, t]);

  const totalStepCount = steps.length;

  const onInstallSubmit = useCallback(
    (values: IHubPackageDeployment) => {
      if (wizardStep !== totalStepCount) {
        return;
      }
      const pkgId = hubCartPackage?.packageId;
      if (
        pkgId == null ||
        pkgId <= 0 ||
        userInfo?.companyId == null ||
        userInfo.companyId <= 0
      ) {
        toast.add({
          title: t("hub.subscribe_wizard.install_missing_context", {
            defaultValue: "Missing package or company. Refresh and try again.",
          }),
          data: { type: "error" },
        });
        return;
      }
      const applicationId = values.applicationId?.trim() ?? "";
      const tenantName = values.tenantName?.trim() ?? "";
      const resolved =
        applicationId.length > 0 && tenantName.length > 0
          ? { applicationId, tenantName }
          : null;
      if (!resolved) {
        toast.add({
          title: t("hub.subscribe_wizard.tenant_resolve_error", {
            defaultValue: "Could not resolve the selected tenant.",
          }),
          data: { type: "error" },
        });
        return;
      }

      const defaultConfig = buildDefaultCatalogInstallConfigs({
        solution: numericSolutionCatalogId > 0 ? catalog : undefined,
        addons: addonList,
        companyId: userInfo.companyId,
        catalogTypes,
      });
      const submittedConfig = values.config ?? [];

      const body: IHubPackageDeployment = {
        name: values.name,
        description: values.description,
        packageId: pkgId,
        companyId: userInfo.companyId,
        tenantName: resolved.tenantName,
        applicationId: resolved.applicationId.trim(),
        targetSolutionId: values.targetSolutionId,
        replaceTargetEntity: values.replaceTargetEntity ?? false,
        config: defaultConfig.map((row, index) => ({
          ...row,
          ...submittedConfig[index],
          config: {
            ...row.config,
            ...submittedConfig[index]?.config,
          },
        })),
        status: HubPackageDeploymentStatus.PENDING,
        installedCatalogs: catalogIdsFromPackage.map(
          (c: { catalogId: number; version: number }) => ({
            catalogId: c.catalogId,
            catalogVersion: c.version,
          }),
        ),
      };
      createDeployment({ body, companyId: userInfo?.companyId ?? 0 });
    },
    [
      addonList,
      catalog,
      catalogTypes,
      createDeployment,
      hubCartPackage?.packageId,
      numericSolutionCatalogId,
      t,
      toast,
      totalStepCount,
      userInfo?.companyId,
      wizardStep,
    ],
  );

  useEffect(() => {
    setWizardStep((s) => Math.min(s, totalStepCount));
  }, [totalStepCount]);

  const addonCount = addonList.length;
  const firstAddonStepValue = numericSolutionCatalogId > 0 ? 2 : 1;
  const deploymentStepValue = firstAddonStepValue + addonCount;

  const handleStepChange = useCallback(
    async (nextStep: number) => {
      if (nextStep > wizardStep) {
        const isValid = await form.trigger();
        if (!isValid) return;
      }
      setWizardStep(nextStep);
      setHighestReachedStep((prev) => Math.max(prev, nextStep));
    },
    [form, wizardStep],
  );

  const installPageFooter = useMemo(() => {
    const isLastStep = wizardStep >= totalStepCount;
    return (
      <div className="flex w-full justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigateBack(`/${Paths.CatalogManager}?tab=explorer`)}>
          {t("hub.administer.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={wizardStep <= 1 || isCreatingDeployment}
          onClick={() => void handleStepChange(Math.max(1, wizardStep - 1))}>
          {t("auth.login.back", { defaultValue: "Back" })}
        </Button>
        {isLastStep ? (
          <Button
            type="button"
            loading={isCreatingDeployment}
            onClick={() => void form.handleSubmit(onInstallSubmit)()}>
            {isCreatingDeployment ? (
              <Spinner className="mr-2 size-4 text-current" />
            ) : null}
            {t("hub.package_manager.breadcrumb_install", {
              defaultValue: "Install",
            })}
          </Button>
        ) : (
          <Button
            type="button"
            disabled={isCreatingDeployment}
            onClick={() =>
              void form.handleSubmit(() => handleStepChange(wizardStep + 1))()
            }>
            {t("auth.login.next", { defaultValue: "Next" })}
          </Button>
        )}
      </div>
    );
  }, [
    form,
    isCreatingDeployment,
    navigateBack,
    onInstallSubmit,
    t,
    totalStepCount,
    wizardStep,
    handleStepChange,
  ]);

  if (numericSolutionCatalogId <= 0 && (addonCatalogIds?.length ?? 0) === 0) {
    return null;
  }

  return (
    <WithPermission permissions={[HUB_PERM.CATALOG_BUY]}>
      <Form {...form}>
        <form
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (wizardStep === totalStepCount) {
              void form.handleSubmit(onInstallSubmit)();
            }
          }}>
          <StepperWizard
            orientation="vertical"
            value={wizardStep}
            onValueChange={handleStepChange}
            completedSteps={completedSteps}
            steps={steps}
            isSubmitting={isLoadingCatalogData || isCreatingDeployment}
            progressLabel={t("hub.create_catalog.progress", {
              defaultValue: "Progress",
            })}>
            <AppContentFooter>{installPageFooter}</AppContentFooter>
            {numericSolutionCatalogId > 0 && (
              <StepperWizard.Step value={1}>
                <CatalogTemplateStep
                  forms={[
                    ...(catalogType?.formId && catalogType.formId > 0
                      ? [
                          {
                            formId: catalogType.formId,
                            type: "catalogType" as const,
                            namePrefix: `config.${0}.config.${
                              catalog
                                ? resolveCatalogInstallTypeKey(
                                    catalog,
                                    catalogTypes,
                                  )
                                : ""
                            }`,
                            label: t(
                              "hub.create_catalog.catalog_type_configuration",
                              {
                                defaultValue: "Catalog Type Configuration",
                              },
                            ),
                          },
                        ]
                      : []),
                    ...(catalog?.formId && catalog.formId > 0
                      ? [
                          {
                            formId: catalog.formId,
                            type: "catalog" as const,
                            namePrefix: `config.${0}.config.${resolveCatalogInstallTypeKey(
                              catalog,
                              catalogTypes,
                            )}`,
                            label: t(
                              "hub.create_catalog.catalog_configuration",
                              {
                                defaultValue: "Catalog Configuration",
                              },
                            ),
                          },
                        ]
                      : []),
                  ]}
                  form={form}
                  title={t("hub.subscribe_wizard.step_solution_details_desc", {
                    defaultValue: "Solution configuration",
                  })}
                  description={t(
                    "hub.subscribe_wizard.solution_details_intro",
                    {
                      defaultValue:
                        "Configure tuning parameters for the solution.",
                    },
                  )}
                  isFieldsDisabled={false}
                  emptyMessage={t("hub.subscribe_wizard.no_form_associated", {
                    defaultValue:
                      "No integration form is associated with this catalog.",
                  })}
                />
              </StepperWizard.Step>
            )}
            {addonList.map((addon: IHubCatalog, index: number) => {
              const addonCatalogType = catalogTypes.find(
                (ct) => ct.typeId === addon.typeId,
              );
              const forms = [];
              const configRowIndex =
                (numericSolutionCatalogId > 0 ? 1 : 0) + index;
              const namePrefix = `config.${configRowIndex}.config.${resolveCatalogInstallTypeKey(
                addon,
                catalogTypes,
              )}`;

              if (addonCatalogType?.formId && addonCatalogType.formId > 0) {
                forms.push({
                  formId: addonCatalogType.formId,
                  type: "catalogType" as const,
                  namePrefix,
                  label: t("hub.create_catalog.catalog_type_configuration", {
                    defaultValue: "Catalog Type Configuration",
                  }),
                });
              }
              if (addon.formId && addon.formId > 0) {
                forms.push({
                  formId: addon.formId,
                  type: "catalog" as const,
                  namePrefix,
                  label: t("hub.create_catalog.catalog_configuration", {
                    defaultValue: "Catalog Configuration",
                  }),
                });
              }

              return (
                <StepperWizard.Step
                  key={
                    addon.catalogId != null
                      ? `addon-${addon.catalogId}`
                      : `addon-${addon.token}-${index}`
                  }
                  value={firstAddonStepValue + index}>
                  <CatalogTemplateStep
                    title={hubCatalogDisplayName(addon, index)}
                    description={t("hub.subscribe_wizard.addon_details_intro", {
                      name: hubCatalogDisplayName(addon, index),
                      defaultValue:
                        "Configure tuning parameters for the {{name}} add-on.",
                    })}
                    forms={forms}
                    form={form}
                    isFieldsDisabled={false}
                    emptyMessage={t("hub.subscribe_wizard.no_form_associated", {
                      defaultValue: "No form is associated with this catalog.",
                    })}
                  />
                </StepperWizard.Step>
              );
            })}
            <StepperWizard.Step value={deploymentStepValue}>
              <PricingDeploymentStep
                control={form.control}
                setValue={form.setValue}
                applicationTenants={tenants ?? []}
                companyUuid={companyUuid}
                numericSolutionCatalogId={numericSolutionCatalogId}
              />
            </StepperWizard.Step>
          </StepperWizard>
        </form>
      </Form>
    </WithPermission>
  );
}
